import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Trash2, TrendingDown, AlertTriangle, Euro, Loader2, Flame, AlertCircle } from 'lucide-react';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar, Cell } from 'recharts';

const RAISON_COLORS = {
  'DLC depassee': '#ef4444',
  'Erreur commande': '#f59e0b',
  'Cuisson ratee': '#eab308',
  'Chute / Casse': '#8b5cf6',
  'Non specifie': '#64748b',
};

export function PertesPage() {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    try { const res = await api.get('/insights/pertes'); setData(res.data); }
    catch { toast.error('Erreur chargement'); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { fetch(); }, [fetch]);

  if (loading || !data) return <div className="flex justify-center h-96 items-center"><Loader2 className="w-8 h-8 animate-spin" /></div>;

  return (
    <div className="space-y-5" data-testid="pertes-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Flame className="w-6 h-6 text-red-600" /> Compteur de Pertes
        </h1>
        <p className="text-muted-foreground text-sm">Valorisation du gaspillage via tes prix Mercuriale</p>
      </div>

      {/* Big Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard label="Aujourd'hui" value={`${data.cout_today}€`} icon={Euro} color="text-blue-600" />
        <StatCard label="7 derniers jours" value={`${data.cout_7j}€`} icon={Euro} color="text-amber-600" />
        <StatCard label="30 derniers jours" value={`${data.cout_30j}€`} icon={Euro} color="text-red-600"
                  sub={`${data.nb_pertes_30j} pertes`} big />
        <StatCard label="% du CA gaspille" value={`${data.pct_gaspillage_ca}%`} icon={AlertTriangle}
                  color={data.pct_gaspillage_ca > 5 ? 'text-red-600' : data.pct_gaspillage_ca > 2 ? 'text-amber-600' : 'text-green-600'}
                  sub={data.pct_gaspillage_ca > 5 ? 'Trop eleve' : data.pct_gaspillage_ca > 2 ? 'Attention' : 'Maitrise'} />
        <StatCard label="Couverture Mercuriale" value={`${data.pct_matching || 0}%`} icon={AlertCircle}
                  color={(data.pct_matching || 0) >= 70 ? 'text-green-600' : (data.pct_matching || 0) >= 40 ? 'text-amber-600' : 'text-red-600'}
                  sub={`${data.nb_unmatched || 0} sans prix`} />
      </div>

      {/* Timeline 30j */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><TrendingDown className="w-4 h-4 text-red-600" /> Evolution sur 30 jours</CardTitle></CardHeader>
        <CardContent>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.serie_30j}>
                <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                <XAxis dataKey="date" fontSize={10} tickFormatter={(d) => d.slice(5)} />
                <YAxis fontSize={10} unit="€" />
                <Tooltip formatter={(v) => `${v}€`} labelStyle={{ color: '#1a1a2e' }} />
                <Line type="monotone" dataKey="cout" stroke="#ef4444" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <div className="grid md:grid-cols-2 gap-4">
        {/* Top produits gaspilles */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Top produits gaspilles (30j)</CardTitle></CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Produit</TableHead>
                <TableHead className="text-right">Quantite</TableHead>
                <TableHead className="text-right">Cout</TableHead>
                <TableHead className="text-right">Occurrences</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {data.top_produits_gaspilles.length === 0 ? <TableRow><TableCell colSpan={4} className="text-center py-6 text-muted-foreground">Aucun gaspillage</TableCell></TableRow> :
                  data.top_produits_gaspilles.map((t, i) => (
                    <TableRow key={i}>
                      <TableCell className="font-medium">{t.ingredient}</TableCell>
                      <TableCell className="text-right text-xs">{t.quantite_totale} {t.unite}</TableCell>
                      <TableCell className="text-right font-bold text-red-600">{t.cout.toFixed(2)}€</TableCell>
                      <TableCell className="text-right text-xs text-muted-foreground">{t.occurrences}x</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Par raison */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Repartition par raison (30j)</CardTitle></CardHeader>
          <CardContent>
            {data.par_raison.length === 0 ? <p className="text-center py-8 text-muted-foreground text-sm">Aucune donnee</p> : (
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.par_raison} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                    <XAxis type="number" fontSize={10} unit="€" />
                    <YAxis type="category" dataKey="raison" fontSize={10} width={100} />
                    <Tooltip formatter={(v) => `${v}€`} />
                    <Bar dataKey="cout" radius={[0, 4, 4, 0]}>
                      {data.par_raison.map((r, i) => <Cell key={i} fill={RAISON_COLORS[r.raison] || '#64748b'} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Historique recent */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Trash2 className="w-4 h-4" /> Dernieres pertes enregistrees</CardTitle></CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Produit</TableHead>
              <TableHead>Zone</TableHead>
              <TableHead className="text-right">Quantite</TableHead>
              <TableHead className="text-right">Cout estime</TableHead>
              <TableHead>Raison</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {data.recent.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center py-6 text-muted-foreground">Aucune perte enregistree</TableCell></TableRow> :
                data.recent.map(p => (
                  <TableRow key={p.id}>
                    <TableCell className="text-sm">{p.date}</TableCell>
                    <TableCell className="font-medium">{p.ingredient}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{p.zone}</TableCell>
                    <TableCell className="text-right text-xs">{p.quantite} {p.unite}</TableCell>
                    <TableCell className="text-right font-bold text-red-600">{p.cout_estime.toFixed(2)}€</TableCell>
                    <TableCell><Badge variant="outline" style={{ borderColor: RAISON_COLORS[p.raison] || '#94a3b8' }}>{p.raison || '—'}</Badge></TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
          <div className="p-3 text-xs text-muted-foreground text-center">
            Pour ajouter une perte, utilise le module <b>Inventaire</b> → Produits jetes
          </div>
        </CardContent>
      </Card>

      {/* Unmatched items */}
      {data.unmatched && data.unmatched.length > 0 && (
        <Card className="border-amber-200 bg-amber-50/30" data-testid="unmatched-pertes-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-amber-600" />
              Pertes sans prix Mercuriale ({data.unmatched.length})
            </CardTitle>
            <p className="text-xs text-muted-foreground">
              Ces produits n'ont pas de correspondance dans la Mercuriale, leur cout n'est donc pas valorise.
              Importe une facture qui les contient pour activer le calcul.
            </p>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Produit</TableHead>
                <TableHead className="text-right">Occurrences</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {data.unmatched.map((u, i) => (
                  <TableRow key={i} data-testid={`unmatched-perte-${i}`}>
                    <TableCell className="font-medium">{u.ingredient}</TableCell>
                    <TableCell className="text-right">
                      <Badge variant="outline">{u.occurrences}x</Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function StatCard({ label, value, sub, icon: Icon, color, big }) {
  return (
    <Card className={big ? 'border-2' : ''}>
      <CardContent className="pt-3 pb-2.5">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs text-muted-foreground">{label}</span>
          <Icon className={`w-4 h-4 ${color}`} />
        </div>
        <p className={`${big ? 'text-3xl' : 'text-2xl'} font-bold tabular-nums ${color}`}>{value}</p>
        {sub && <p className="text-[10px] text-muted-foreground mt-0.5">{sub}</p>}
      </CardContent>
    </Card>
  );
}
